import { UnitAddress } from 'newTypes/unit'
import { GeoPoint } from 'types/osmAddress'

/** Public listing row: `location` string and/or `address` (API `Unit`). */
export type ListingAddressSource = {
  id?: string
  location?: string | null
  address?: UnitAddress
}

function trimPart(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

export function listingGeoPoint(property: ListingAddressSource): GeoPoint | null {
  const addr = property.address
  if (!addr || typeof addr !== 'object') return null

  const loc = addr.location ?? addr.geometry?.location
  if (!loc || typeof loc !== 'object') return null

  const lat = Number((loc as { lat?: unknown }).lat)
  const lng = Number((loc as { lng?: unknown }).lng)

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng }
  }

  return null
}

function fuzzGeoPoint(point: GeoPoint, seed: string): GeoPoint {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }

  const latOffset = ((hash % 100) - 50) / 5000
  const lngOffset = (((hash >> 8) % 100) - 50) / 5000

  return {
    lat: point.lat + latOffset,
    lng: point.lng + lngOffset,
  }
}

export function listingGeoPointForDisplay(
  property: ListingAddressSource,
  revealExact = false
): GeoPoint | null {
  const exact = listingGeoPoint(property)
  if (!exact) return null
  if (revealExact) return exact

  const seed = property.id || `${exact.lat},${exact.lng}`
  return fuzzGeoPoint(exact, seed)
}

/** City and neighborhood only — safe for cards and public listing views. */
export function listingPublicAddressLine(property: ListingAddressSource): string {
  const addr = property.address
  if (addr && typeof addr === 'object') {
    const neighborhood = trimPart(addr.neighborhood)
    const city = trimPart(addr.city)
    return [neighborhood, city].filter(Boolean).join(', ')
  }

  return ''
}

/** Full street address — only after application, contact, or for owners. */
export function listingFullAddressLine(property: ListingAddressSource): string {
  const direct = trimPart(property.location)
  if (direct) return direct

  const addr = property.address
  if (!addr || typeof addr !== 'object') return ''

  return [
    addr.street,
    addr.number && `No. ${addr.number}`,
    addr.doorNumber && `Apt ${addr.doorNumber}`,
    addr.neighborhood,
    addr.city,
    addr.province,
  ]
    .filter(Boolean)
    .join(', ')
}

/** @deprecated Use listingPublicAddressLine for public UI. */
export function listingAddressLine(property: ListingAddressSource): string {
  return listingPublicAddressLine(property)
}

export function listingDisplayAddressLine(
  property: ListingAddressSource,
  revealFullAddress: boolean
): string {
  if (revealFullAddress) {
    return listingFullAddressLine(property) || listingPublicAddressLine(property)
  }

  return listingPublicAddressLine(property)
}
